import qflcstartimage from '@/assets/qflcstartimage.png';
import { adjustPageNo, isDeepEqual } from '@/util';
import BlockerDialog from '@components/BlockerDialog';
import ButtonRow from '@components/ButtonRow';
import ContactsTable from '@components/ContactsTable';
import FilterWarningDialog from '@components/FilterWarningDialog';
import OtherActionsPanelContent from '@components/OtherActionsPanelContent';
import SaveListDialog from '@components/SaveListDialog';
import SaveSearchDialog from '@components/SaveSearchDialog';
import UpdateSearchDialog from '@components/SaveSearchDialog/UpdateSearchDialog';
import Sidebar from '@components/Sidebar';
import { Box, Button, Tooltip, Typography } from '@mui/material';
import FilterPanelContent from './FilterPanelContent';
import { filterInitialState } from './filterCriteriaReducer';
import { LIST_SIZE_LIMIT, SUPPORTER_DATA_LOADING } from './quickFilterListCreatorConstants';
import { useQuickFilterListCreatorPage } from './useQuickFilterListCreatorPage';

type QuickFilterListCreatorLayoutProps = ReturnType<typeof useQuickFilterListCreatorPage>;

export default function QuickFilterListCreatorLayout({
  accordionStates,
  setAccordionStates,
  filterCriteria,
  dispatchActionAndClearDrawerText,
  isDisableApply,
  isDisableSaveButton,
  handleApplyFilter,
  handleSaveSearchDialogOpen,
  isFetching,
  opendrawerLeft,
  setopendrawerLeft,
  drawerTitle,
  isSearchModified,
  enablePicture,
  listPreview,
  currentPage,
  setCurrentPage,
  pageSize,
  setPageSize,
  columnVisibilityModel,
  setColumnVisibilityModel,
  sortModel,
  setSortModel,
  filterModel,
  setFilterModel,
  contactIds,
  setContactIds,
  contactIdUrl,
  disableSelectAll,
  userDetails,
  handleOtherActions,
  rightDrawIsOpen,
  filterChangedSinceLastPreview,
  handleSaveToListDialogOpen,
  isSaveListDialogOpen,
  handleSaveToListDialogClose,
  searchQFilter,
  saveSearchDialogStatus,
  setSaveSearchDialogStatus,
  handleSaveSearchDialogClose,
  searchMetadata,
  isUpdateSearchDialogOpen,
  setIsUpdateSearchDialogOpen,
  setIsSearchModified,
  openFilterWarning,
  missingFilters,
  setOpenFilterWarning,
  blocker,
  popStateBlock,
  allowNextNavigation,
  handleOtherActionsClose,
}: QuickFilterListCreatorLayoutProps) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        height: "inherit",
        justifyContent: "space-between"
      }}>
      <Sidebar
        isOpen={opendrawerLeft}
        setIsOpen={setopendrawerLeft}
        title={
          <Box
            sx={{
              display: 'flex ',
              flexDirection: 'row',
              flexShrink: 1,
              overflow: 'hidden',
            }}
          >
            <Tooltip title={drawerTitle} placement="right">
              <Typography
                component="h2"
                variant="h5"
                sx={[
                  {
                    flexShrink: 1,
                    maxHeight: '2rem',
                    transition: 'opacity 0.3s',
                    overflow: 'hidden',
                    textWrap: 'nowrap',
                    textOverflow: 'ellipsis',
                  },
                  opendrawerLeft
                    ? {
                        opacity: '1',
                      }
                    : {
                        opacity: '0',
                      },
                ]}
              >
                {drawerTitle}
              </Typography>
            </Tooltip>
            {isSearchModified && searchMetadata.name !== undefined ? (
              <Typography
                variant="h5"
                sx={[
                  {
                    maxHeight: '2rem',
                    transition: 'opacity 0.3s',
                  },
                  opendrawerLeft
                    ? {
                        opacity: '1',
                      }
                    : {
                        opacity: '0',
                      },
                ]}
              >
                *
              </Typography>
            ) : (
              <></>
            )}
          </Box>
        }
        position="left"
        testId="leftSidePanel"
        openPanelVisible
        noTopBorder
        noDropShadow
      >
        <FilterPanelContent
          handleApplyFilter={handleApplyFilter}
          filterCriteria={filterCriteria}
          dispatchFilterCriteria={dispatchActionAndClearDrawerText}
          isDisableApply={isDisableApply}
          isDisableSave={isDisableSaveButton || isDeepEqual(filterCriteria, filterInitialState)}
          handleSaveFilter={handleSaveSearchDialogOpen}
          accordionStates={accordionStates}
          setAccordionStates={setAccordionStates}
          isApplying={isFetching}
        />
      </Sidebar>
      <Box
        tabIndex={0}
        sx={{
          flexGrow: 1,
          maxWidth: '2000px',
          padding: '2rem',
          overflow: "auto"
        }}>
        {enablePicture && (
          <Box sx={{ textAlign: 'center' }}>
            <p>
              <img src={qflcstartimage} alt="This is the quick search page." />
            </p>
            <Typography component="h2" variant="h5">
              Please add some filters to build your search.
            </Typography>
          </Box>
        )}
        {!enablePicture && (
          <>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "2rem"
              }}>
              <Typography component="h2" variant="h5">
                Results
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "1rem"
                }}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between"
                  }}>
                  {isFetching ? (
                    <Typography>
                      <em>{SUPPORTER_DATA_LOADING}</em>
                    </Typography>
                  ) : (
                    <Typography>{listPreview?.count ?? 0} supporters found</Typography>
                  )}
                </Box>
                <ContactsTable
                  isLoading={isFetching}
                  data={!isFetching ? (listPreview?.entities ?? []) : []}
                  paginationModel={{ page: currentPage, pageSize: pageSize }}
                  onPaginationModelChange={(model) => {
                    setPageSize(model.pageSize);
                    setCurrentPage(
                      model.pageSize !== pageSize
                        ? adjustPageNo(pageSize, currentPage, model.pageSize)
                        : model.page
                    );
                  }}
                  columnVisibilityModel={columnVisibilityModel}
                  onColumnVisibilityModelChange={(newModel) => setColumnVisibilityModel(newModel)}
                  onSortModelChange={(model) => {
                    setSortModel(model);
                  }}
                  sortModel={sortModel}
                  onFilterModelChange={(model) => {
                    setFilterModel(model);
                  }}
                  filterModel={filterModel}
                  checkboxSelection
                  rowSelectionModel={contactIds}
                  setSelectionModel={(value) => setContactIds(value)}
                  idUrl={contactIdUrl}
                  disableSelectAll={disableSelectAll}
                  idWithoutEmptyFilters
                  filterPhoneOnFullNumber
                />
                <ButtonRow sx={{ margin: 0 }}>
                  {['Coordinator', 'Principal Coordinator'].includes(
                    userDetails?.userLevel ?? 'None'
                  ) && (
                    <Button
                      variant="outlined"
                      disabled={
                        listPreview?.count === 0 ||
                        isFetching ||
                        rightDrawIsOpen ||
                        filterChangedSinceLastPreview
                      }
                      onClick={handleOtherActions}
                    >
                      Other Actions
                    </Button>
                  )}
                  <Button
                    variant="contained"
                    onClick={handleSaveToListDialogOpen}
                    disabled={
                      (listPreview?.count ?? 0) === 0 ||
                      isFetching ||
                      rightDrawIsOpen ||
                      filterChangedSinceLastPreview
                    }
                  >
                    Save To List
                  </Button>
                </ButtonRow>
              </Box>
            </Box>
          </>
        )}

        <SaveListDialog
          isOpen={isSaveListDialogOpen}
          onClose={handleSaveToListDialogClose}
          qFilter={searchQFilter}
          contactIds={contactIds.length === 0 ? undefined : contactIds}
          key={isSaveListDialogOpen ? 'saveListOpen' : 'saveListClosed'}
          isTruncateContacts={
            contactIds.length > LIST_SIZE_LIMIT ||
            (contactIds.length === 0 && (listPreview?.count ?? 0) > LIST_SIZE_LIMIT)
          }
          onAllowNavigationBeforeLeave={allowNextNavigation}
        />
        <SaveSearchDialog
          dialogStatus={saveSearchDialogStatus}
          onClose={handleSaveSearchDialogClose}
          search={filterCriteria}
          key={saveSearchDialogStatus}
          currentSearchMetadata={searchMetadata}
          searchType="Quick"
        />
        <UpdateSearchDialog
          isOpen={isUpdateSearchDialogOpen}
          onClose={(saved) => {
            setIsUpdateSearchDialogOpen(false);
            if (saved) setIsSearchModified(false);
          }}
          openSaveSearchDialog={({ prefill }: { prefill: boolean }) => {
            setSaveSearchDialogStatus(prefill ? 'prefilled' : 'open');
          }}
          search={filterCriteria}
          searchMetadata={searchMetadata}
        />
        <FilterWarningDialog
          open={openFilterWarning}
          filternames={missingFilters}
          closeHandler={() => {
            setOpenFilterWarning(false);
          }}
        />
        <BlockerDialog blocker={blocker} popStateBlock={popStateBlock} />
      </Box>
      <Sidebar
        isOpen={rightDrawIsOpen}
        title="Other actions"
        testId="rightSidePanel"
        position="right"
        noTopBorder
      >
        <OtherActionsPanelContent
          onClose={handleOtherActionsClose}
          qFilter={{
            contactIds: contactIds.length > 0 ? contactIds : undefined,
            ...searchQFilter,
          }}
          sortOrder={sortModel?.[0]?.sort ?? 'asc'}
          sortColumn={sortModel?.[0]?.field ?? 'id'}
        />
      </Sidebar>
    </Box>
  );
}
